import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Trash2,
  Pencil,
  Plus,
  Users,
  Check,
  ArrowRightLeft,
} from 'lucide-react';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import {
  Badge,
  Button,
  Input,
  Select,
  Modal,
  Loader,
  EMOJI_COLOR_CLASSES,
} from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { EmptyState, ErrorState } from '@/components/states';
import { toast } from '@/stores';
import { householdService, invitationService } from '@/services';
import { InvitationEmailNotSentError } from '@/services/invitation.service';
import { useAppShell, usePermissions } from '@/hooks';
import { SettingsMembersSkeleton } from './skeletons';
import { useUserStore } from '@/stores/userStore';
import {
  getAccessLevelLabel,
  getInviteAccessOptions,
  INVITE_ACCESS_OPTIONS,
  inviteValueToRole,
  roleToInviteValue,
} from '@/constants/accessLevels';
import type { HouseholdMemberWithUser, Invitation } from '@/types';

type View = 'list' | 'add' | 'loading' | 'sent';

/**
 * Whether the transfer-ownership control is offered at all.
 *
 * Off by a product decision (YAR-434), not because the code below is
 * unfinished: transferring a household to someone who already owns or belongs
 * to another one has no agreed answer yet, and QA found the control reporting
 * success while changing nothing. Offering a destructive, irreversible action
 * that silently does nothing is worse than not offering it.
 *
 * Deliberately a flag rather than deleted code. The modal, the handler and the
 * `household-transfer` function stay wired and covered, so re-enabling is this
 * one word — and the diff that re-enables it is reviewable on its own.
 *
 * Two things to fix before flipping this back on:
 *  - `handleTransferOwnership` swallows its error into `console.error` with no
 *    toast, which is why the failure looked like "nothing happened".
 *  - The product rule for a target who already belongs to another household.
 *
 * Hiding this control does not close the capability: `household-transfer` is
 * still callable directly and still enforces owner-only on its own.
 */
const TRANSFER_OWNERSHIP_ENABLED = false;

export function SettingsMembers() {
  const sentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { householdId } = useAppShell();
  const [view, setView] = useState<View>('list');
  const [members, setMembers] = useState<HouseholdMemberWithUser[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>(
    [],
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Current user info
  const currentUserId = useUserStore((s) => s.userId);
  // Member management is a manager capability (Admin + Co-Admin); ownership
  // transfer stays owner-only. RLS is the real enforcement — this only
  // decides which controls to render.
  const { canManageMembers, isOwner } = usePermissions();

  // Delete modal state
  const [deleteTarget, setDeleteTarget] =
    useState<HouseholdMemberWithUser | null>(null);

  // Revoke invitation state (deletes the caregiver_invitations row).
  const [revokingInvitationId, setRevokingInvitationId] = useState<
    string | null
  >(null);

  // Compute how many PRIMARY members remain so we can block removing the
  // last one — the household must always have at least one owner.
  const primaryCount = members.filter((m) => m.role === 'PRIMARY').length;

  // Transfer ownership modal state
  const [transferTarget, setTransferTarget] =
    useState<HouseholdMemberWithUser | null>(null);
  const [transferring, setTransferring] = useState(false);

  // Edit-access modal state (change an existing member's access level).
  const [editAccessTarget, setEditAccessTarget] =
    useState<HouseholdMemberWithUser | null>(null);
  const [editAccessValue, setEditAccessValue] = useState('');
  const [updatingAccess, setUpdatingAccess] = useState(false);

  // Edit-access modal state for a still-PENDING invitation. Separate state
  // from the member modal above: the row is an invitation, not a membership,
  // and it saves through a different service call.
  const [editInviteTarget, setEditInviteTarget] = useState<Invitation | null>(
    null,
  );
  const [editInviteValue, setEditInviteValue] = useState('');
  const [updatingInvite, setUpdatingInvite] = useState(false);

  // Add / Edit form state
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addAccess, setAddAccess] = useState('');

  // Co-Admin is offered only where the environment enables it — see
  // isCoAdminRoleEnabled(). Inviting is a clean filter.
  const inviteOptions = useMemo(() => getInviteAccessOptions(), []);
  // Editing is NOT. A member (or a pending invite) that already holds
  // Co-Admin must keep seeing it as their current value, or the Select
  // renders with a value absent from its options and saving would silently
  // demote them. Hiding a role from new grants must never rewrite an
  // existing one.
  const optionsIncludingCurrent = useCallback(
    (value: string) => {
      if (value !== 'co_admin') return inviteOptions;
      if (inviteOptions.some((o) => o.value === 'co_admin'))
        return inviteOptions;
      const coAdmin = INVITE_ACCESS_OPTIONS.find((o) => o.value === 'co_admin');
      return coAdmin ? [coAdmin, ...inviteOptions] : inviteOptions;
    },
    [inviteOptions],
  );
  const editAccessOptions = useMemo(
    () => optionsIncludingCurrent(editAccessValue),
    [optionsIncludingCurrent, editAccessValue],
  );
  const editInviteOptions = useMemo(
    () => optionsIncludingCurrent(editInviteValue),
    [optionsIncludingCurrent, editInviteValue],
  );

  /* ------------------------------------------------------------------ */
  /*  Data loading                                                       */
  /* ------------------------------------------------------------------ */

  const loadMembers = useCallback(async (hid: string) => {
    setLoadError(false);
    try {
      const [data, pending] = await Promise.all([
        householdService.getMembers(hid),
        invitationService.getPendingByHousehold(hid),
      ]);
      setMembers(data);
      setPendingInvitations(pending);
    } catch (err) {
      console.error('Failed to load members:', err);
      setLoadError(true);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!householdId) {
      setInitialLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadMembers(householdId);
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, loadMembers]);

  useEffect(() => {
    return () => {
      if (sentTimerRef.current) clearTimeout(sentTimerRef.current);
    };
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Handlers                                                           */
  /* ------------------------------------------------------------------ */

  const openAdd = () => {
    setAddName('');
    setAddEmail('');
    setAddAccess('');
    setView('add');
  };

  const handleSendInvite = async () => {
    if (!canSendInvite || !householdId) return;

    setView('loading');
    try {
      await invitationService.createInvitation(householdId, {
        invitee_email: addEmail.trim(),
        // Required since YAR-312 C9 (all three fields gate the CTA).
        invitee_name: addName.trim(),
        role: inviteValueToRole(addAccess),
      });
      setView('sent');
      toast.success('Invite sent');
      sentTimerRef.current = setTimeout(() => {
        setView('list');
        if (householdId) loadMembers(householdId);
      }, 2000);
    } catch (err) {
      console.error('Failed to send invitation:', err);
      // The form stays filled either way, so "try again" is one tap — and a
      // retry of a saved-but-unsent invite is safe (it mails a fresh link).
      toast.error(
        err instanceof InvitationEmailNotSentError
          ? err.message
          : 'Failed to send invite',
      );
      setView('add');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    // Defense-in-depth: the UI disables the trash icon when only one
    // PRIMARY remains, but the modal can still be open from a stale
    // render. Re-check here before hitting the service.
    if (deleteTarget.role === 'PRIMARY' && primaryCount <= 1) {
      toast.error('Cannot remove the only household owner');
      setDeleteTarget(null);
      return;
    }
    try {
      await householdService.removeMember(deleteTarget.id);
      setDeleteTarget(null);
      toast.success('Member removed');
      if (householdId) loadMembers(householdId);
    } catch (err) {
      console.error('Failed to remove member:', err);
      toast.error('Failed to remove member');
      setDeleteTarget(null);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setRevokingInvitationId(invitationId);
    try {
      await invitationService.revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      if (householdId) await loadMembers(householdId);
    } catch (err) {
      console.error('Failed to revoke invitation:', err);
      toast.error('Failed to revoke invitation');
    } finally {
      setRevokingInvitationId(null);
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferTarget || !householdId) return;
    setTransferring(true);
    try {
      await householdService.transferOwnership(
        householdId,
        transferTarget.user_id,
      );
      setTransferTarget(null);
      if (householdId) loadMembers(householdId);
    } catch (err) {
      console.error('Failed to transfer ownership:', err);
    } finally {
      setTransferring(false);
    }
  };

  const openEditAccess = (member: HouseholdMemberWithUser) => {
    setEditAccessValue(roleToInviteValue(member.role));
    setEditAccessTarget(member);
  };

  const handleUpdateAccess = async () => {
    if (!editAccessTarget || editAccessValue === '') return;
    setUpdatingAccess(true);
    try {
      await householdService.updateMemberRole(
        editAccessTarget.id,
        inviteValueToRole(editAccessValue),
      );
      setEditAccessTarget(null);
      toast.success('Access updated');
      if (householdId) loadMembers(householdId);
    } catch (err) {
      console.error('Failed to update member access:', err);
      toast.error('Failed to update access');
    } finally {
      setUpdatingAccess(false);
    }
  };

  const openEditInvite = (invitation: Invitation) => {
    setEditInviteValue(roleToInviteValue(invitation.role));
    setEditInviteTarget(invitation);
  };

  const handleUpdateInviteAccess = async () => {
    if (!editInviteTarget || editInviteValue === '') return;
    setUpdatingInvite(true);
    try {
      await invitationService.updateInvitationRole(
        editInviteTarget.id,
        inviteValueToRole(editInviteValue),
      );
      setEditInviteTarget(null);
      toast.success('Invite updated');
      if (householdId) loadMembers(householdId);
    } catch (err) {
      console.error('Failed to update invitation access:', err);
      toast.error('Failed to update invite');
    } finally {
      setUpdatingInvite(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  const accessLabel = (role: string | null) => getAccessLevelLabel(role);

  // The owner (Admin) badge stays visually distinct from the rest.
  const accessVariant = (role: string): 'primary' | 'default' =>
    role === 'PRIMARY' ? 'primary' : 'default';

  // Figma draws the neutral access tags ("Can edit" / "View only") on a white
  // fill; Badge's `default` variant ships the #f9f9f9 `surface-secondary`
  // grey (YAR-230). The `!` is load-bearing: `--color-background` is declared
  // BEFORE `--color-surface-secondary` in tokens.css, so Tailwind emits
  // `.bg-background` first and an unimportant override would lose the
  // cascade. Only the neutral variant is repainted — the owner's `primary`
  // badge keeps its tinted fill.
  const NEUTRAL_BADGE_CLASS = '!bg-background';

  // YAR-312 C9: Name, Email and Access level are all required (YAR-148
  // AC7), matching onboarding's collaborator form.
  const canSendInvite =
    addName.trim().length > 0 && addEmail.trim().length > 0 && addAccess !== '';

  /* ------------------------------------------------------------------ */
  /*  Render: loading view                                               */
  /* ------------------------------------------------------------------ */

  if (view === 'loading') {
    return (
      <SettingsSubPage title="Add member">
        <div className="flex items-center justify-center min-h-[60dvh]">
          <Loader
            size="lg"
            label="Sending invite..."
          />
        </div>
      </SettingsSubPage>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: sent view                                                  */
  /* ------------------------------------------------------------------ */

  if (view === 'sent') {
    return (
      <SettingsSubPage title="Add member">
        <div className="flex flex-1 flex-col items-center justify-center min-h-[60dvh] gap-3 px-5">
          {/* Figma uses the success green (#17825a) for this confirmation
              tick, not the brand green (#264133) — the two are easy to
              confuse at a glance (YAR-230). */}
          <Check
            size={32}
            className="text-success"
          />
          <p className="text-xl font-medium text-foreground text-center">
            Invites sent
          </p>
        </div>
      </SettingsSubPage>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: add view                                                   */
  /* ------------------------------------------------------------------ */

  if (view === 'add') {
    return (
      <SettingsSubPage
        title="Add member"
        onBack={() => setView('list')}
        stickyFooter={
          <Button
            variant="primary"
            fullWidth
            disabled={!canSendInvite}
            onClick={handleSendInvite}
          >
            Send invite
          </Button>
        }
      >
        <div className="ph-no-capture flex flex-col gap-5 px-5 py-6">
          <Input
            label="Name"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="First and last name"
          />
          <Input
            label="Email"
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            placeholder="email@example.com"
          />
          <Select
            label="Access level"
            placeholder="Select access level"
            value={addAccess}
            onChange={(e) => setAddAccess(e.target.value)}
            options={inviteOptions}
          />
        </div>
      </SettingsSubPage>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: list view (default)                                        */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <SettingsSubPage title="Household members">
        {initialLoading ? (
          <SettingsMembersSkeleton />
        ) : loadError ? (
          <ErrorState
            message="Unable to load household members"
            onRetry={() => {
              if (householdId) loadMembers(householdId);
            }}
          />
        ) : members.length === 0 ? (
          <EmptyState
            icon={<Users size={40} />}
            title="No members yet"
            description="Invite a family member to share this household."
            action={
              canManageMembers ? (
                <Button
                  variant="primary"
                  onClick={openAdd}
                >
                  Invite member
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="ph-no-capture flex flex-col gap-3 px-5 py-5">
            {members.map((member) => {
              // A manager (Admin or Co-Admin) may edit or remove any
              // non-owner member — never themselves, never the owner. RLS
              // is the real gate; this only decides which affordances show.
              const canEditMember =
                canManageMembers &&
                member.user_id !== currentUserId &&
                member.role !== 'PRIMARY';
              const memberInfo = (
                <>
                  {/* `min-w-0` + `truncate`: an owner's row now carries three
                      icon buttons, so a long display name has to yield rather
                      than push the access badge out of the card. */}
                  <div className="flex gap-1 items-center min-w-0">
                    <span className="text-base font-medium text-foreground truncate">
                      {member.users?.display_name ?? 'Unknown'}
                    </span>
                    <Badge
                      variant={accessVariant(member.role)}
                      size="sm"
                      className={
                        accessVariant(member.role) === 'default'
                          ? `shrink-0 ${NEUTRAL_BADGE_CLASS}`
                          : 'shrink-0'
                      }
                    >
                      <span className="text-xs">
                        {accessLabel(member.role)}
                      </span>
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground truncate">
                    {member.users?.email ?? ''}
                  </span>
                </>
              );
              return (
                <div
                  key={member.id}
                  // 2px `border-strong` (#d1d1d1), not the 1px `border`
                  // (#e5e5e5) hairline — QA filed the card outline as too
                  // light and too faint (YAR-230). Matches ChildListItem.
                  className="flex gap-2 items-center rounded-xl border-2 border-border-strong px-4 py-3"
                >
                  {/* YAR-312 C9: avatar per row (YAR-148 AC1), same anatomy
                      as ChildListItem so the two rosters match. */}
                  <ProfileAvatar
                    imagePath={member.users?.avatar_image_path ?? null}
                    emoji={member.users?.avatar_emoji ?? null}
                    color={member.users?.avatar_color ?? EMOJI_COLOR_CLASSES[0]}
                    initials={member.users?.display_name
                      ?.charAt(0)
                      .toUpperCase()}
                    alt={member.users?.display_name ?? ''}
                    size="lg"
                  />
                  {canEditMember ? (
                    <button
                      type="button"
                      onClick={() => openEditAccess(member)}
                      className="flex flex-col gap-0.5 flex-1 min-w-0 text-left cursor-pointer"
                      aria-label={`Edit access for ${member.users?.display_name ?? 'member'}`}
                    >
                      {memberInfo}
                    </button>
                  ) : (
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      {memberInfo}
                    </div>
                  )}
                  {canEditMember && (
                    <div className="flex items-center shrink-0">
                      {/* The pencil edits the member's ACCESS LEVEL — the same
                          action as tapping the row. It used to open the
                          transfer-ownership modal instead (YAR-379): the Figma
                          redesign swapped this button's ArrowRightLeft icon for
                          a Pencil but left the transfer handler attached, so
                          "edit" was one confirmation away from handing the
                          household over. Icon, label and handler must agree. */}
                      <button
                        type="button"
                        onClick={() => openEditAccess(member)}
                        className="p-2.5 cursor-pointer"
                        aria-label={`Edit access for ${member.users?.display_name ?? 'member'}`}
                      >
                        <Pencil
                          size={24}
                          className="text-muted-foreground"
                        />
                      </button>
                      {/* Transfer ownership is owner-only (a Co-Admin never
                          hands out ownership) and keeps its own distinct
                          icon so it can never be mistaken for a plain edit.
                          Currently hidden — see TRANSFER_OWNERSHIP_ENABLED. */}
                      {TRANSFER_OWNERSHIP_ENABLED && isOwner && (
                        <button
                          type="button"
                          onClick={() => setTransferTarget(member)}
                          className="p-2.5 cursor-pointer"
                          aria-label={`Transfer ownership to ${member.users?.display_name ?? 'member'}`}
                        >
                          <ArrowRightLeft
                            size={24}
                            className="text-muted-foreground"
                          />
                        </button>
                      )}
                      {/* A manager (Admin or Co-Admin) may remove any
                          non-owner member. The owner (PRIMARY) is never
                          removable here — ownership moves only via transfer. */}
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(member)}
                        className="p-2.5 cursor-pointer"
                        aria-label={`Remove ${member.users?.display_name ?? 'member'}`}
                      >
                        <Trash2
                          size={24}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Invite member dashed card — managers (Admin + Co-Admin) only. */}
            {canManageMembers && (
              <button
                type="button"
                onClick={openAdd}
                className="border border-dashed border-border-strong rounded-xl flex items-center justify-center h-16 w-full cursor-pointer"
              >
                <span className="text-sm font-semibold text-muted-foreground">
                  Invite member
                </span>
                <Plus
                  size={16}
                  className="ml-1.5 text-muted-foreground"
                />
              </button>
            )}

            {/* Invite Sent section */}
            {pendingInvitations.length > 0 && (
              <div className="flex flex-col gap-2 mt-2">
                <h3 className="text-base font-medium text-foreground">
                  Invite Sent
                </h3>
                <div className="border border-border rounded-lg overflow-hidden">
                  {pendingInvitations.map((inv) => (
                    <div
                      key={inv.id}
                      className="flex gap-2 items-center px-4 py-3 border-b border-border last:border-b-0"
                    >
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <div className="flex gap-1 items-center min-w-0">
                          <span className="text-base font-medium text-foreground truncate">
                            {inv.invitee_email.split('@')[0]}
                          </span>
                          <Badge
                            variant="default"
                            size="sm"
                            className={`shrink-0 ${NEUTRAL_BADGE_CLASS}`}
                          >
                            <span className="text-xs">
                              {accessLabel(inv.role)}
                            </span>
                          </Badge>
                          {/* YAR-312 C9: pending status visible on the row
                              (YAR-148 AC10), alongside the access level the
                              invite will grant. */}
                          <Badge
                            variant="warning"
                            size="sm"
                            className="shrink-0"
                          >
                            <span className="text-xs">Pending</span>
                          </Badge>
                        </div>
                        <span className="text-sm text-muted-foreground truncate">
                          {inv.invitee_email}
                        </span>
                      </div>
                      <div className="flex items-center shrink-0">
                        {/* A pending invite carries the same pencil as a
                            committed member row (Figma; QA filed it missing
                            on YAR-230). It edits the access level the invite
                            will grant — the invitee hasn't accepted yet, so
                            there is no membership to edit, only the invite.
                            Manager-gated to match the member rows; RLS
                            (`caregiver_invitations_manager_write`) is the
                            real gate. */}
                        {canManageMembers && (
                          <button
                            type="button"
                            onClick={() => openEditInvite(inv)}
                            className="p-2.5 cursor-pointer"
                            aria-label={`Edit access for ${inv.invitee_email}`}
                          >
                            <Pencil
                              size={24}
                              className="text-muted-foreground"
                            />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRevokeInvitation(inv.id)}
                          disabled={revokingInvitationId === inv.id}
                          className={[
                            'p-2.5',
                            revokingInvitationId === inv.id
                              ? 'cursor-not-allowed opacity-40'
                              : 'cursor-pointer',
                          ].join(' ')}
                          aria-label={`Revoke invitation for ${inv.invitee_email}`}
                        >
                          <Trash2
                            size={24}
                            className="text-muted-foreground"
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SettingsSubPage>

      {/* Remove member confirmation. Layout, tones and copy follow Figma
          (Settings / household members, node 7771:59693 — modal 7771:59824;
          YAR-230): no header bar or ✕, a centred 20px heading-tone title, the
          16px body with the member's name in bold, then the red outline
          Remove stacked over a plain Cancel — the same shape as the Leave
          household and Remove child confirmations. */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        ariaLabel="Remove member"
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          <div className="flex flex-col gap-1.5 w-full text-center">
            <p className="text-xl leading-7 font-medium text-text-heading break-words">
              {`Remove ${deleteTarget?.users?.display_name ?? 'member'}?`}
            </p>
            <p className="text-base leading-6 text-text-body break-words">
              Are you sure you want to remove{' '}
              <span className="font-bold">
                {deleteTarget?.users?.display_name ?? 'this member'}
              </span>{' '}
              from the household? They will lose access immediately.
            </p>
          </div>
          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="destructiveOutline"
              fullWidth
              onClick={handleDelete}
            >
              Remove
            </Button>
            <Button
              variant="tertiary"
              fullWidth
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Transfer ownership confirmation modal */}
      <Modal
        open={transferTarget !== null}
        onClose={() => setTransferTarget(null)}
        title="Transfer ownership?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setTransferTarget(null)}
              disabled={transferring}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleTransferOwnership}
              loading={transferring}
            >
              Transfer
            </Button>
          </>
        }
      >
        <p className="ph-no-capture text-sm text-muted-foreground">
          This will make{' '}
          <span className="font-medium text-foreground">
            {transferTarget?.users?.display_name ?? 'this member'}
          </span>{' '}
          the new household owner with full access. Your role will change to
          view-only. This action cannot be undone without the new owner
          transferring back.
        </p>
      </Modal>

      {/* Edit member access-level modal. PRIMARY (owner) is never an option
          here — ownership moves only via Transfer. RLS re-checks the change. */}
      <Modal
        open={editAccessTarget !== null}
        onClose={() => setEditAccessTarget(null)}
        title={`Edit access for ${editAccessTarget?.users?.display_name ?? 'member'}`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setEditAccessTarget(null)}
              disabled={updatingAccess}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateAccess}
              loading={updatingAccess}
              disabled={editAccessValue === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="ph-no-capture px-1 py-2">
          <Select
            label="Access level"
            value={editAccessValue}
            onChange={(e) => setEditAccessValue(e.target.value)}
            options={editAccessOptions}
          />
        </div>
      </Modal>

      {/* Edit the access level a still-PENDING invitation will grant. The
          magic link already in the invitee's inbox stays valid — accept
          reads the role off the row at accept time, so no re-invite is
          needed. */}
      <Modal
        open={editInviteTarget !== null}
        onClose={() => setEditInviteTarget(null)}
        title={`Edit access for ${editInviteTarget?.invitee_email ?? 'invite'}`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setEditInviteTarget(null)}
              disabled={updatingInvite}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateInviteAccess}
              loading={updatingInvite}
              disabled={editInviteValue === ''}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="ph-no-capture px-1 py-2">
          <Select
            label="Access level"
            value={editInviteValue}
            onChange={(e) => setEditInviteValue(e.target.value)}
            options={editInviteOptions}
          />
        </div>
      </Modal>
    </>
  );
}
